import React, { useMemo, useState } from 'react'
import { AnalyticsMonthPoint, AnalyticsPoint } from '../lib/api'

/** 色板与 PIE_COLORS / 设计稿协调：cards 蓝、opens 绿 */
const COLOR_CARDS = '#2557A7'
const COLOR_OPENS = '#16A085'

type Props = {
  data: AnalyticsPoint[] | AnalyticsMonthPoint[]
  xKey: 'date' | 'month'
}

// 视图区几何（viewBox 坐标系，preserveAspectRatio="none" 拉伸适配）
const W = 800
const H = 220
const PAD_L = 40
const PAD_R = 10
const PAD_T = 20
const PAD_B = 30
const INNER_W = W - PAD_L - PAD_R
const INNER_H = H - PAD_T - PAD_B // = 170，底轴 y=190

const xLabel = (v: string, xKey: 'date' | 'month'): string =>
  xKey === 'month' ? v : v.slice(5) // YYYY-MM-DD → MM-DD

/**
 * 迷你双折线图（dashboard_analytics_v1）：纯 SVG，无图表库依赖。
 * 蓝线=留位卡生成数（带 10% 面积），绿线=链接打开数（带 8% 面积），hover 显示当天/当月数值 tooltip。
 */
const MiniLineChart: React.FC<Props> = ({ data, xKey }) => {
  const [hover, setHover] = useState<number | null>(null)

  const { pts, maxV, xTicks } = useMemo(() => {
    const n = data.length
    const max = Math.max(1, ...data.map((d) => Math.max(d.cards, d.opens)))
    const x = (i: number) => (n <= 1 ? PAD_L + INNER_W / 2 : PAD_L + (i / (n - 1)) * INNER_W)
    const y = (v: number) => PAD_T + INNER_H - (v / max) * INNER_H
    const points = data.map((d, i) => ({
      x: x(i),
      yc: y(d.cards),
      yo: y(d.opens),
      label: xLabel((xKey === 'month' ? (d as AnalyticsMonthPoint).month : (d as AnalyticsPoint).date) ?? '', xKey),
      raw: xKey === 'month' ? (d as AnalyticsMonthPoint).month : (d as AnalyticsPoint).date,
      cards: d.cards,
      opens: d.opens,
      visitors: d.visitors,
    }))
    // x 轴刻度：首/尾 + 中间均布，最多 5 个
    const tickIdx: number[] = []
    if (n > 0) {
      const wanted = Math.min(5, n)
      for (let k = 0; k < wanted; k++) {
        const idx = Math.round((k * (n - 1)) / Math.max(1, wanted - 1))
        if (!tickIdx.includes(idx)) tickIdx.push(idx)
      }
    }
    return {
      pts: points,
      maxV: max,
      xTicks: tickIdx.map((i) => ({ x: x(i), label: points[i].label })),
    }
  }, [data, xKey])

  if (pts.length === 0) return null

  const polyline = (key: 'yc' | 'yo') => pts.map((p) => `${p.x.toFixed(1)},${p[key].toFixed(1)}`).join(' ')
  const area = (key: 'yc' | 'yo') =>
    `${polyline(key)} ${pts[pts.length - 1].x.toFixed(1)},${PAD_T + INNER_H} ${pts[0].x.toFixed(1)},${PAD_T + INNER_H}`

  // y 轴网格：0 / max/4 / max/2 / max*3/4 四条虚线 + 顶线
  const gridVals = [0, 0.25, 0.5, 0.75].map((r) => Math.round(maxV * r))
  const gridY = (v: number) => PAD_T + INNER_H - (v / maxV) * INNER_H

  return (
    <div style={{ position: 'relative', height: 220, borderBottom: '1px solid #f0f0f0', marginBottom: 16 }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '100%' }}>
        {/* 轴与网格 */}
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + INNER_H} stroke="#f0f0f0" />
        <line x1={PAD_L} y1={PAD_T + INNER_H} x2={W - PAD_R} y2={PAD_T + INNER_H} stroke="#f0f0f0" />
        {gridVals.slice(1).map((v) => (
          <g key={v}>
            <line x1={PAD_L} y1={gridY(v)} x2={W - PAD_R} y2={gridY(v)} stroke="#f0f0f0" strokeDasharray="3 3" />
            <text x={8} y={gridY(v) + 4} fontSize={10} fill="#7F8C8D">{v}</text>
          </g>
        ))}
        <text x={8} y={gridY(0) + 4} fontSize={10} fill="#7F8C8D">0</text>

        {/* 面积 + 折线：cards 蓝、opens 绿 */}
        <polygon points={area('yc')} fill={COLOR_CARDS} opacity={0.1} />
        <polyline points={polyline('yc')} fill="none" stroke={COLOR_CARDS} strokeWidth={2} />
        <polygon points={area('yo')} fill={COLOR_OPENS} opacity={0.08} />
        <polyline points={polyline('yo')} fill="none" stroke={COLOR_OPENS} strokeWidth={2} />

        {/* x 轴刻度 */}
        {xTicks.map((t, i) => (
          <text
            key={`${t.label}-${i}`}
            x={t.x}
            y={H - 8}
            fontSize={10}
            fill="#7F8C8D"
            textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
          >
            {t.label}
          </text>
        ))}

        {/* hover 竖线与圆点 */}
        {hover !== null && pts[hover] && (
          <g>
            <line x1={pts[hover].x} y1={PAD_T} x2={pts[hover].x} y2={PAD_T + INNER_H} stroke="#d9d9d9" strokeDasharray="3 3" />
            <circle cx={pts[hover].x} cy={pts[hover].yc} r={3.5} fill={COLOR_CARDS} />
            <circle cx={pts[hover].x} cy={pts[hover].yo} r={3.5} fill={COLOR_OPENS} />
          </g>
        )}

        {/* 透明命中列（每点一列，mouseenter 即停） */}
        {pts.map((p, i) => {
          const prevX = i === 0 ? PAD_L : (pts[i - 1].x + p.x) / 2
          const nextX = i === pts.length - 1 ? W - PAD_R : (p.x + pts[i + 1].x) / 2
          return (
            <rect
              key={i}
              x={prevX}
              y={PAD_T}
              width={Math.max(4, nextX - prevX)}
              height={INNER_H}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          )
        })}
      </svg>

      {/* tooltip（HTML 浮层，随命中列定位） */}
      {hover !== null && pts[hover] && (
        <div
          style={{
            position: 'absolute',
            left: `${(pts[hover].x / W) * 100}%`,
            top: 0,
            transform: `translateX(${pts[hover].x / W > 0.6 ? '-110%' : '10%'})`,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 12,
            lineHeight: '18px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          <div style={{ fontWeight: 600 }}>{pts[hover].raw}</div>
          <div>留位卡生成数：{pts[hover].cards}</div>
          <div>链接打开数：{pts[hover].opens}</div>
          <div>打开人数：{pts[hover].visitors}</div>
        </div>
      )}
    </div>
  )
}

export default MiniLineChart
